import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { useCallback, useReducer } from 'react';

import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';

// Original class: mobileCreateField
const StyledMobileCreateField = styled(TextField)(theme => ({
  height: '100%',

  '& .MuiInputBase-root': {
    backgroundColor: '#fff',
    height: '100%',
    '&:hover': {
      backgroundColor: '#fff',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
    '&.Mui-disabled': {
      backgroundColor: '#fff',
    },
    '& input': {
      fontSize: 14,
      textAlign: 'center',
      color: '#212346',
    },
  },
}));
// Original class: mobileCommentLabel
const StyledMobileCommentLabel = styled(Box)(({ theme }) => ({
  '&.MuiInputLabel-root': {
    ...theme.typography.h4,
    top: -2,
    color: '#7174AC',
    '&[data-shrink="true"]': {
      ...theme.typography.h5,
      color: '#111322',
      top: -24,
      transform: 'unset',
    },
  },
}));
// Original class: clearButton
const StyledClearButton = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    zIndex: -1,
  },
}));
const SpreadsheetMobileCreateRow = ({
  day,
  intl,
  currentWeek,
  currentIssue,
  disabled,
  setWeekSum,
  timeType,
  timeTypes,
  setEditedFormattedIssues,
  type,
}) => {
  const comment = intl.formatMessage({ id: 'comment' });
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const handleChangeDayHour = useCallback(
    e => {
      const regex = /^([01]?[0-9]|2[0-4])([,.][0-9]{1,2})?$/;
      const currentDay = currentWeek.find(day => day.date === e.target.name);
      if (type !== 'edit') {
        currentWeek[currentWeek.indexOf(currentDay)].redmineId =
          currentDay.redmineId;
        currentWeek[currentWeek.indexOf(currentDay)].issueId =
          currentIssue.issueId;
      }
      currentWeek[currentWeek.indexOf(currentDay)].isEdited =
        type === 'edit'
          ? true
          : !(
              Number(e.target.value) === 0 ||
              e.target.value.length === 0 ||
              !regex.test(e.target.value)
            );
      currentWeek[currentWeek.indexOf(currentDay)].hours =
        regex.test(e.target.value) || e.target.value === ''
          ? Number(e.target.value)
          : '';
      setEditedFormattedIssues(prevState => ({
        ...prevState,
        [currentIssue.issueUniqueId]: {
          ...currentIssue,
          [timeType === timeTypes.MAIN_HOURS ? 'weekDays' : 'overTimeWeekDays']:
            currentWeek,
        },
      }));
      setWeekSum(currentWeek.reduce((acc, day) => acc + Number(day.hours), 0));
      forceUpdate();
    },
    [
      currentIssue,
      currentWeek,
      setEditedFormattedIssues,
      setWeekSum,
      timeType,
      timeTypes.MAIN_HOURS,
      type,
    ],
  );

  const handleChangeDayComment = useCallback(
    e => {
      const currentDay = currentWeek.find(day => day.date === e.target.name);
      currentWeek[currentWeek.indexOf(currentDay)].comments = e.target.value;
      setEditedFormattedIssues(prevState => ({
        ...prevState,
        [currentIssue.issueUniqueId]: {
          ...currentIssue,
          [timeType === timeTypes.MAIN_HOURS ? 'weekDays' : 'overTimeWeekDays']:
            currentWeek,
        },
      }));
      forceUpdate();
    },
    [
      currentIssue,
      currentWeek,
      setEditedFormattedIssues,
      timeType,
      timeTypes.MAIN_HOURS,
    ],
  );

  const handleClearDay = useCallback(
    e => {
      const currentDay = currentWeek.find(day => day.date === e.target.value);
      const currentDayIndex = currentWeek.indexOf(currentDay);
      if (currentDay.hours !== 0 || currentDay.comments.length !== 0) {
        currentWeek[currentWeek.indexOf(currentDay)].isEdited = true;
        currentWeek[currentDayIndex].hours = 0;
        currentWeek[currentDayIndex].comments = '';
        setWeekSum(
          currentWeek.reduce((acc, day) => acc + Number(day.hours), 0),
        );
        setEditedFormattedIssues(prevState => ({
          ...prevState,
          [currentIssue.issueUniqueId]: {
            ...currentIssue,
            [timeType === timeTypes.MAIN_HOURS
              ? 'weekDays'
              : 'overTimeWeekDays']: currentWeek,
          },
        }));
      }
      forceUpdate();
    },
    [
      currentIssue,
      currentWeek,
      setEditedFormattedIssues,
      setWeekSum,
      timeType,
      timeTypes.MAIN_HOURS,
    ],
  );

  return (
    <Grid
      container
      direction='column'
      sx={{
        marginTop: 2.5,
      }}
    >
      <Box
        sx={{
          marginBottom: 1,
        }}
      >
        <Typography variant='h5'>
          {day.dayOfWeek}, {dayjs(day.date).format('DD.MM')}
        </Typography>
      </Box>
      <Grid container wrap='nowrap'>
        <Box
          sx={{
            marginRight: 2.5,
            maxWidth: 65,
            height: 48,
          }}
        >
          <StyledMobileCreateField
            variant='filled'
            onChange={handleChangeDayHour}
            value={day.hours}
            name={day.date}
            color={day.hours === 0 ? '#7174AC' : '#212346'}
            disabled={disabled}
          />
        </Box>
        <Box
          sx={{
            height: 48,
            width: '100%',
          }}
        >
          <StyledMobileCreateField
            variant='filled'
            label={comment}
            onChange={handleChangeDayComment}
            value={day.comments}
            name={day.date}
            disabled={disabled || day.hours === 0}
            slotProps={{
              inputLabel: { component: StyledMobileCommentLabel },
            }}
          />
        </Box>
        {type === 'edit' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: 40,
            }}
          >
            <StyledClearButton
              disableRipple
              onClick={handleClearDay}
              value={day.date}
            >
              <ClearIcon
                width={24}
                height={24}
                viewBox='0 0 24 24'
                value={day.date}
              />
            </StyledClearButton>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default SpreadsheetMobileCreateRow;
